import { MessageDescriptor } from '@lingui/core';
import { defineMessage, t } from '@lingui/core/macro';
import { type Quantity } from '@luminovo/api-contracts';
import { MonetaryValue } from '@luminovo/commons';
import { AppLinkOptions, CheckableChipProps, TooltipVariant } from '@luminovo/design-system';
import { FormattingType } from '@luminovo/frontend-utils';
import { TableCellProps, TableRowProps } from '@mui/material';
import { FallbackRender } from '@sentry/react';
import type { Atom } from '@tanstack/react-store';
// The augmentation below reshapes these, so they have to name the real module.
import { AccessorFn, ColumnMeta, TableMeta, TableOptions } from '@tanstack/react-table';
import React, { type JSX } from 'react';
import { TableComponents } from 'react-virtuoso';

import { CommandDefinition } from './CommandPalette/types';
import {
    Column,
    ColumnDef,
    ColumnFiltersState,
    ColumnOrderState,
    ColumnSizingState,
    ColumnVisibilityState,
    DataStackFeatures,
    ExpandedState,
    Row,
    RowData,
    RowSelectionState,
    SortingState,
    Table,
} from './tanstackTypes';

export type RenderType = 'text' | 'ellipsisOnly' | 'number' | 'generic';

/**
 * Where a computed column's value comes from.
 *
 * `fields` names the row fields it reads, so a consumer knows what to look at;
 * `derivation` says in words what it does with them, because the useful consumer
 * here is a language model rather than an evaluator, and prose it already
 * understands beats a grammar we would have to invent and it would have to
 * implement.
 */
export type ColumnSource = { fields: string[]; derivation: string } | { unavailable: string };

export type MonetaryValueRenderOption = 'default' | 'unit-price';

export const MONETARY_VALUE_RENDER_OPTIONS: Array<{
    id: MonetaryValueRenderOption;
    label: () => string;
}> = [
    { id: 'default', label: () => t`2 decimals` },
    { id: 'unit-price', label: () => t`5 decimals` },
];

/** `real-unit` keeps the original unit (500 mm); `base-unit` normalizes it (0.5 m). */
export type QuantityRenderOption = 'base-unit' | 'real-unit';

export const QUANTITY_RENDER_OPTIONS: Array<{
    id: QuantityRenderOption;
    label: () => string;
}> = [
    { id: 'real-unit', label: () => t`Original unit` },
    { id: 'base-unit', label: () => t`Normalized` },
];

/** `weeks` rounds up, to half weeks below 10 weeks: see `formatAvailabilityDuration`. */
export type AvailabilityRenderOption = 'days' | 'weeks';

export const AVAILABILITY_RENDER_OPTIONS: Array<{
    id: AvailabilityRenderOption;
    label: () => string;
}> = [
    { id: 'days', label: () => t`Days` },
    { id: 'weeks', label: () => t`Weeks` },
];

/** App-wide display preferences; entries move here as per-table preferences become global. */
export interface DisplayFormat {
    leadTime: AvailabilityRenderOption;
}

export type RenderOptionConfig<TRenderOption extends string = FormattingType> = {
    default: TRenderOption;
    /** Stops users from changing the option in the header menu. */
    disabled?: boolean;
};

/** Column ID to render option, kept in localStorage. */
export type ColumnRenderOptionsState = Record<string, string>;

/**
 * Defines how to render empty values in a column.
 * - `() => string`: A function that returns a placeholder text to display (e.g., `() => "-"`, `() => t\`Unknown\``)
 * - `(value: TValue) => React.ReactElement`: A custom render function that receives the empty value
 * - `{ placeholder: (() => string) | ((value: TValue) => React.ReactElement), isEmptyValue?: (value: TValue) => boolean }`: An object with the placeholder and optional custom empty detection function
 * - `undefined`: the default, a dash in neutral color
 *
 * By default, empty values are considered: null, undefined, empty string (""), and empty arrays ([]).
 */
export type EmptyCell<TValue> =
    | (() => string)
    | ((value: TValue) => React.ReactElement)
    | {
          placeholder: (() => string) | ((value: TValue) => React.ReactElement);
          isEmptyValue?: (value: TValue) => boolean;
      }
    | undefined;

type AdditionalFilterBase = {
    id: string;
    label: () => string;
    description?: () => string | { title: () => string; variant?: TooltipVariant };
    quickFilters?: QuickFilter<any>[];
    enableAdvancedFilters?: boolean;
};

export type AdditionalFilter<TData extends RowData, TSharedContext = unknown> =
    | (AdditionalFilterBase & {
          dataType: 'text';
          accessor: AccessorFn<TData, string | null | undefined>;
      })
    | (AdditionalFilterBase & {
          dataType: 'number';
          accessor: AccessorFn<TData, number | null | undefined>;
      })
    | (AdditionalFilterBase & {
          dataType: 'monetaryValue';
          accessor: AccessorFn<TData, MonetaryValue | null | undefined>;
      })
    | (AdditionalFilterBase & {
          dataType: 'date';
          accessor: AccessorFn<TData, string | null | undefined>;
      })
    | (AdditionalFilterBase & {
          dataType: 'enum';
          accessor: AccessorFn<TData>;
          getOptionLabel: (option: any) => string;
          options?:
              | Array<any>
              | ((props: {
                    column: Column<TData, any>;
                    facetedValues: Array<any>;
                    sharedContext: TSharedContext;
                }) => Array<any>);
          renderOption?: (option: any) => JSX.Element;
          getOptionCount?: (option: any, sharedContext: TSharedContext) => number | undefined;
          getOptionKey?: (option: any) => string | number | boolean;
      })
    | (AdditionalFilterBase & {
          dataType: 'array';
          accessor: AccessorFn<TData>;
          getOptionLabel: (option: any) => string;
          options?:
              | Array<unknown>
              | ((props: {
                    column: Column<TData, any>;
                    facetedValues: Array<unknown>;
                    sharedContext: TSharedContext;
                }) => Array<unknown>);
          renderOption?: (option: any) => JSX.Element;
          getOptionCount?: (option: any, sharedContext: TSharedContext) => number | undefined;
          getOptionKey?: (option: any) => string | number | boolean;
      })
    | (AdditionalFilterBase & {
          dataType: 'quantity';
          accessor: AccessorFn<TData, Quantity | null | undefined>;
      });

// No constraints or variance here: TypeScript merges an unconstrained re-declaration, but not a differing one.
declare module '@tanstack/table-core' {
    // oxlint-disable-next-line no-unused-vars
    interface CellContext<TFeatures, TData, TValue, TSharedContext = unknown> {
        sharedContext: TSharedContext;
        TableCellProps: TableCellProps;
        /** The user's render option, else the default; typed for monetary columns, others narrow `cell`. */
        renderOption: FormattingType;
    }

    // oxlint-disable-next-line no-unused-vars
    interface ColumnMeta<TFeatures, TData, TValue, TShareContext = unknown> {
        label: () => string;

        /**
         * Where this column's value comes from, for something that has to rebuild
         * the column without running this code — an app exporting the view, say.
         *
         * A column declared with a string accessor needs none of this: the accessor
         * already names the row field. Declare it when the value is computed, or
         * when it does not come from the row at all.
         *
         * @example
         *   source: {
         *       fields: ['customer_name', 'customer_number'],
         *       derivation: 'the customer name and number, joined by a space',
         *   }
         *
         * @example
         *   source: { unavailable: 'comes from GET /project-progress, keyed by rfq_id' }
         */
        source?: ColumnSource;

        /**
         * Helper text in the column header.
         *
         * @example
         *   description: () => ({ title: () => "This is the user's username", variant: 'white' })
         */
        description?: () => string | { title: () => string | JSX.Element; variant?: TooltipVariant };

        /** Adds these quick filters to the toolbar. */
        quickFilters?: Array<QuickFilter<unknown>>;

        /** Shows the advanced filter menu; on by default. */
        enableAdvancedFilters?: boolean;

        /** Transforms the filter value before it's sent to the backend. */
        filterValueTransform?: (value: unknown) => unknown;

        align?: 'left' | 'center' | 'right';

        /** Pins the column from the start, instead of `initialState.columnPinning`. Needs an `id`. */
        initialPinning?: 'start' | 'end';

        /** `false` hides the column from the start, instead of `initialState.columnVisibility`. Needs an `id`. */
        initialVisibility?: boolean;

        /** `false` keeps row clicks out of this column; the default for `action` columns. */
        enableOnRowClick?: boolean;

        /** Puts the row's link here, not in the first column that takes row clicks; its content is the link text. */
        // Mark the column that names the row, and keep controls out of it. If it's hidden, the default applies.
        rowLink?: boolean;

        /** Leaves `cell` to render its own `TableCell`, with `TableCellProps`. */
        disableTableCell?: boolean;

        filterConfig?: FilterConfig<TData, TValue, TShareContext>;

        /** Overrides the render type, e.g. to render a column as one type but filter it as another. */
        renderType?: RenderType | ((item: TData) => RenderType);

        /**
         * Allows to override the default ellipsis text for `text`-rendered columns.
         * Can be useful in case the default value (obtained from the `textContent` of the cell) is not what you want.
         * For example, if you use chips and `textContent` returns no whitespace between values.
         */
        getEllipsisText?: (row: TData) => string | undefined;

        /** @internal Decides how the column sorts, filters and renders. */
        dataType:
            | 'text'
            | 'number'
            | 'enum'
            | 'array'
            | 'date'
            | 'monetaryValue'
            | 'quantity'
            | 'availability'
            | 'generic';

        /**
         * Defines how to render empty values in a column.
         * - `() => string`: A function that returns a placeholder text to display (e.g., `() => "-"`, `() => t\`Unknown\``)
         * - `(value: TValue) => React.ReactElement`: A custom render function that receives the empty value
         * - `{ placeholder: (() => string) | ((value: TValue) => React.ReactElement), isEmptyValue?: (value: TValue) => boolean }`: An object with the placeholder and optional custom empty detection function
         * - `undefined`: the default, a dash in neutral color
         *
         * By default, empty values are considered: null, undefined, empty string (""), and empty arrays ([]).
         */
        emptyCell?: EmptyCell<TValue>;

        /** Lets users change how values display from the column header menu. */
        renderOptionConfig?: RenderOptionConfig;

        /** As passed to the column helper; `useColumnDefs` turns each into a hidden column. */
        additionalFilters?: AdditionalFilter<TData, TShareContext>[];

        /** IDs of hidden columns generated from additionalFilters. Set automatically by useColumnDefs. */
        additionalFilterColumnIds?: string[];

        /** ID of the parent column this additional filter belongs to. Set automatically by useColumnDefs. */
        parentColumnId?: string;

        /** Whether this column is a generated additional filter column. Set automatically by useColumnDefs. */
        isAdditionalFilter?: boolean;
    }

    // oxlint-disable-next-line no-unused-vars
    interface TableMeta<TFeatures, TData, TSharedContext = unknown> {
        /** Data and callbacks that every cell can reach through its context. */
        sharedContext: TSharedContext;

        /**
         * Used to show the total count of rows in the table. This is useful when the table is paginated and the total count is not known upfront.
         */
        totalCount?: number;

        /**
         * @example
         *   onRowClick: (row, table) => console.log('Row clicked:', row)
         */
        onRowClick?: (row: Row<TData>, table: Table<TData>) => void;

        /** The row's link and one tab stop; clicks off its controls follow it. `null` falls back to `onRowClick`. */
        getRowRoute?: (row: Row<TData>, table: Table<TData>) => AppLinkOptions | null;

        /** Prepends a selection column: `column`, or checkboxes by default. */
        enableSelection?: {
            enabled: boolean;

            /**
             * Indicates if you can select more than one row or not
             *
             * @default true
             */
            enableMultiSelection?: boolean;
            /** Row IDs, which also key the selection that `enablePersistentRowSelection` keeps. */
            getRowId: TableOptions<DataStackFeatures, TData>['getRowId'];
            column?: ColumnDef<TData, any>;
            /**
             * Callback function that is called when the row selection changes.
             *
             * Please use a stable reference for the callback function.
             */
            onRowSelectionChange?: (value: RowSelectionState) => void;
        };

        /** Keys what this table persists, such as its column order and visibility. */
        columnsKey: string;

        /** @internal */
        getIsLoading: () => boolean;

        /** React Query's `isLoadingError` (failed, no data yet): the table shows "Try again" instead of a spinner. */
        isLoadingError?: boolean;

        /** Called by "Try again", usually the query's `refetch`. */
        onRetry?: () => void;

        /**
         * Callback function that is triggered when the table fetches the next page. This function should fetch the next page of data and update the table data.
         */
        fetchNextPage?: () => void;

        /**
         * Indicates whether the table is fetching the next page. When true, the table displays a loading indicator at the bottom.
         */
        isFetchingNextPage: boolean;
        enableColumnHiding: boolean;

        enableColumnOrdering: boolean;

        /** Keeps column filters and sorting in the URL. */
        enablePersistentColumnFilters: boolean;

        /** Saves filters and sorting as defaults, in session storage unless `storage: 'local'`, which is risky. */
        enableSaveAsDefault:
            | false
            | string
            | {
                  storage: 'local' | 'session';
                  key: string;
              };

        /** Saves the column order in localStorage, with `enableColumnOrdering`; on by default. */
        enablePersistentColumnOrder: boolean;

        /** Saves column visibility in localStorage, with `enableColumnHiding`; on by default. */
        enablePersistentColumnVisibility: boolean;

        /** Saves column widths in localStorage. */
        enablePersistentColumnSizing: boolean;

        enableColumnResizing: boolean;

        /** Keeps the row selection in session storage, under this key. */
        enablePersistentRowSelection: false | string;
        /** @deprecated Probably broken. Keeps expanded rows in session storage, under this key. */
        enablePersistentExpanded: false | string;
        /** Keeps the global filter in the URL. */
        enablePersistentGlobalFilter: boolean;

        /** Back and Forward put the table where it was scrolled on that entry (the router's scroll restoration). */
        enablePersistentScrollPosition: boolean;

        enableExcelExport: boolean;

        /** Managed by `useTanStackTable`. */
        columnRenderOptions: ColumnRenderOptionsState;

        /** Takes a `string`, as it doesn't know the column's type. */
        setColumnRenderOption: (columnId: string, renderOption: string) => void;

        setColumnRenderOptions: (options: ColumnRenderOptionsState) => void;

        /** App-wide, from `DisplayFormatProvider`; availability columns use it instead of `columnRenderOptions`. */
        displayFormat: DisplayFormat;

        setDisplayFormat: (update: Partial<DisplayFormat>) => void;

        /** @internal */
        defaultInitialState: TableOptions<DataStackFeatures, TData>['initialState'];

        /** @internal For `enableSaveAsDefault`. */
        sessionFiltersAndSorting: {
            persistentFilters: ColumnFiltersState;
            setPersistentFilters: (
                newState: ColumnFiltersState | ((value: ColumnFiltersState) => ColumnFiltersState),
            ) => void;
            persistentSorting: SortingState;
            setPersistentSorting: (newState: SortingState | ((value: SortingState) => SortingState)) => void;
        } | null;
    }
}

export type TableSize = 'small' | 'medium' | 'large';

export type UseTanStackTableStateProps<TData extends RowData> = Pick<
    TableMeta<DataStackFeatures, TData, unknown>,
    'columnsKey'
> &
    Pick<
        UseTanStackTableProps<TData>,
        | 'initialState'
        | 'enableSelection'
        | 'enableColumnHiding'
        | 'enableColumnOrdering'
        | 'enablePersistentColumnFilters'
        | 'enablePersistentColumnOrder'
        | 'enablePersistentColumnVisibility'
        | 'enablePersistentColumnSizing'
        | 'enableColumnResizing'
        | 'enablePersistentGlobalFilter'
        | 'enablePersistentRowSelection'
        | 'enableSaveAsDefault'
        | 'enablePersistentExpanded'
    >;

export type TanStackTableState<TData extends RowData> = {
    initialState: TableOptions<DataStackFeatures, TData>['initialState'];

    columnFilters: ColumnFiltersState;
    setColumnFilters: TableOptions<DataStackFeatures, TData>['onColumnFiltersChange'];

    globalFilter: string;
    setGlobalFilter: TableOptions<DataStackFeatures, TData>['onGlobalFilterChange'];

    sorting: SortingState;
    setSorting: TableOptions<DataStackFeatures, TData>['onSortingChange'];

    /** One atom per saved slice, for the table's `atoms`; each writes to storage while its persistence is on. */
    atoms: {
        columnVisibility: Atom<ColumnVisibilityState>;
        columnOrder: Atom<ColumnOrderState>;
        columnSizing: Atom<ColumnSizingState>;
        rowSelection: Atom<RowSelectionState>;
        expanded: Atom<ExpandedState>;
    };

    meta: Pick<
        TableMeta<DataStackFeatures, TData>,
        | 'defaultInitialState'
        | 'columnsKey'
        | 'enableColumnHiding'
        | 'enableColumnOrdering'
        | 'enablePersistentColumnFilters'
        | 'enablePersistentColumnOrder'
        | 'enablePersistentColumnVisibility'
        | 'enablePersistentColumnSizing'
        | 'enableColumnResizing'
        | 'enablePersistentGlobalFilter'
        | 'enablePersistentRowSelection'
        | 'enableSaveAsDefault'
        | 'sessionFiltersAndSorting'
        | 'enablePersistentExpanded'
    >;
};

export type UseTanStackTableProps<TData extends RowData, TSharedContext = unknown> = {
    /** `undefined` shows the loading state; a new array reference reprocesses the data. */
    data: TableOptions<DataStackFeatures, TData>['data'] | undefined;

    columns: ColumnDef<TData, any>[];

    /** Inferred from `data` when undefined. */
    isLoading?: boolean;

    /** Used on resets, and needn't be stable; columns also take `initialVisibility` and `initialPinning`. */
    initialState?: TableOptions<DataStackFeatures, TData>['initialState'];

    /** A boolean for all rows, or a function that decides per row. */
    enableRowSelection?: TableOptions<DataStackFeatures, TData>['enableRowSelection'];

    /**
     * This optional function is used to access the sub rows for any given row. If you are using nested rows,
     * you will need to use this function to return the sub rows object (or undefined) from the row.
     */
    getSubRows?: TableOptions<DataStackFeatures, TData>['getSubRows'];

    filterFromLeafRows?: TableOptions<DataStackFeatures, TData>['filterFromLeafRows'];
} & Partial<
    Omit<TableMeta<DataStackFeatures, TData, TSharedContext>, 'fetchNextPage' | 'isFetchingNextPage' | 'getIsLoading'>
>;

export type UseTanStackTablePaginationProps<TData extends RowData, TSharedContext = unknown> = {
    // `useTanStackTableState` can't know the row type, so the rows and columns decide it.
    tableState: TanStackTableState<NoInfer<TData>>;
    data: TableOptions<DataStackFeatures, TData>['data']; // The pagination table doesn't work correctly with the data undefined.
} & Omit<
    UseTanStackTableProps<TData, TSharedContext>,
    | 'initialState'
    | 'defaultInitialState'
    | 'columnsKey'
    | 'enableColumnHiding'
    | 'enableColumnOrdering'
    | 'enablePersistentColumnFilters'
    | 'enablePersistentColumnOrder'
    | 'enablePersistentColumnVisibility'
    | 'enableColumnResizing'
    | 'enablePersistentGlobalFilter'
    | 'enablePersistentRowSelection'
    | 'data'
    // Row expansion is not wired up in `useTanStackTablePagination`, see its doc comment.
    | 'getSubRows'
    | 'enablePersistentExpanded'
> &
    Required<Pick<TableMeta<DataStackFeatures, TData, TSharedContext>, 'fetchNextPage' | 'isFetchingNextPage'>>;

export type MenuBarVisibility =
    | {
          globalSearch?: boolean;
          quickFilters?: boolean;
          resultCount?: boolean;
          controlButtons?: boolean;
          exportExcelButton?: boolean;
          actionButton?: boolean;
          filterChips?: boolean;
      }
    | boolean;

type HideMenuBarFunction<TData extends RowData = any, TSharedContext = unknown> = (arg: {
    table: Table<TData>;
    sharedContext: TSharedContext;
}) => MenuBarVisibility;

export type TanStackTableProps<TData extends RowData, TSharedContext> = {
    table: Table<TData>;
    size?: TableSize;
    /**
     * The MenuBarTitle component is displayed on the left side of the MenuBar.
     */
    MenuBarTitle?: TanStackTableContext<TData, TSharedContext>['MenuBarTitle'];
    ActionButton?: TanStackTableContext<TData, TSharedContext>['ActionButton'];
    EmptyPlaceholder?: TanStackTableContext<TData, TSharedContext>['EmptyPlaceholder'];
    TableRow?: TanStackTableContext<TData, TSharedContext>['TableRow'];
    overrides?: Partial<TanStackTableOverrides<TData, TSharedContext>>;
    /** Which menu bar elements to show: all or none, per element, or from a function. */
    enableMenuBar?: HideMenuBarFunction<TData, TSharedContext> | MenuBarVisibility;

    /** Shows the empty placeholder even when there's data. Rarely needed. */
    enforceEmptyPlaceholder?: boolean;

    /** While rows are selected, shows a bar with their count and the command palette's bulk actions. */
    enableFloatingSelectionBar?: boolean;

    /** Opened from the bar's Actions button or ⌘K; build them with `createCommandHelper`. */
    commands?: CommandDefinition<TData, TSharedContext>[];

    /** Rendered in the floating bar before the Actions button, e.g. "Export". */
    primarySelectionActionSlot?: React.ReactNode;

    /** The command palette's selection header; defaults to "N items selected". */
    renderSelectionHeader?: (props: { selectedItems: TData[]; isSingleSelection: boolean }) => React.ReactNode;

    /**
     * Custom render function for the floating selection bar's count pill, replacing the default
     * "{count} selected". Use when the count shown should differ from the number of selected rows
     * (e.g. one row that expands to several downstream records); the caller sources its own count.
     * Must return an already-translated node.
     */
    renderSelectionCount?: () => React.ReactNode;

    /**
     * Suppresses the "N selected item(s) are hidden due to active filters" warning in the floating
     * selection bar. Set this when the displayed count is in a different unit than table rows (see
     * `renderSelectionCount`): the hidden count is derived from leaf rows, so showing it alongside
     * a differently-scaled pill would be mixed-unit and misleading.
     * @default false
     */
    suppressHiddenSelectedWarning?: boolean;

    /** Attributes added to the command palette's Sentry metric events. */
    telemetry?: Record<string, string | number | boolean>;
};

export type TanStackTableOverrides<TData extends RowData, TSharedContext> = {
    FallbackComponent?: React.ComponentType<Parameters<FallbackRender>[0] & { onReset: () => void }>;
    Scroller: TableComponents<unknown, TanStackTableContext<TData, TSharedContext>>['Scroller'];
    Table: TableComponents<unknown, TanStackTableContext<TData, TSharedContext>>['Table'];
    OuterTableRow: TableComponents<unknown, TanStackTableContext<TData, TSharedContext>>['TableRow'];
    OuterEmptyPlaceholder: TableComponents<unknown, TanStackTableContext<TData, TSharedContext>>['EmptyPlaceholder'];
};

export type TanStackTableContext<TData extends RowData = any, TSharedContext = unknown> = {
    table: Table<TData>;
    size: TableSize;
    isLoading: boolean;
    TableRow?: React.ComponentType<TableRowProps>;
    MenuBarTitle?: React.ComponentType<{ table: Table<TData>; sharedContext: TSharedContext }>;
    ActionButton?: React.ComponentType<{ table: Table<TData>; sharedContext: TSharedContext }>;
    EmptyPlaceholder?: React.ComponentType<{ table: Table<TData>; sharedContext: TSharedContext }>;
};

export enum SupportedFilterFn {
    includesString = 'includesString',
    isEmptyString = 'isEmptyString',
    isNotEmptyString = 'isNotEmptyString',
    inNumberRange = 'inNumberRange',
    inMonetaryValueRange = 'inMonetaryValueRange',
    inDateRange = 'inDateRange',
    isInLastDays = 'isInLastDays',
    isOlderThanDays = 'isOlderThanDays',
    equalsAny = 'equalsAny',
    equalsNone = 'equalsNone',
    arrIncludesSome = 'arrIncludesSome',
    arrIncludesAll = 'arrIncludesAll',
    arrExcludesSome = 'arrExcludesSome',
    inQuantityRange = 'inQuantityRange',
}
export type NumberRange = [number | null, number | null];
export type DateRange = [string | null, string | null];

/** Days back from now, as `Date.now() - days * 86400 * 1000`; `null` matches all rows, keeping the filter function. */
export type RelativeDateFilterValue = number | null;

export type FilterValue<TValue> =
    | { filterFn: SupportedFilterFn.includesString; value: string }
    | { filterFn: SupportedFilterFn.isEmptyString; value: string }
    | { filterFn: SupportedFilterFn.isNotEmptyString; value: string }
    | { filterFn: SupportedFilterFn.inNumberRange; value: NumberRange }
    | { filterFn: SupportedFilterFn.inMonetaryValueRange; value: NumberRange }
    | { filterFn: SupportedFilterFn.inDateRange; value: DateRange }
    | { filterFn: SupportedFilterFn.isInLastDays; value: RelativeDateFilterValue }
    | { filterFn: SupportedFilterFn.isOlderThanDays; value: RelativeDateFilterValue }
    | { filterFn: SupportedFilterFn.equalsAny; value: TValue }
    | { filterFn: SupportedFilterFn.equalsNone; value: TValue }
    | { filterFn: SupportedFilterFn.arrIncludesSome; value: TValue }
    | { filterFn: SupportedFilterFn.arrIncludesAll; value: TValue }
    | { filterFn: SupportedFilterFn.arrExcludesSome; value: TValue }
    | { filterFn: SupportedFilterFn.inQuantityRange; value: NumberRange };

export type QuickFilter<TValue> = {
    showCount?: boolean;
    label: () => string;
    value: FilterValue<TValue>;
    /** Replaces the column's other filters. */
    replaceExistingFilters?: boolean;

    /**
     * An icon after the chip's label.
     */
    endIcon?: CheckableChipProps['endIcon'];
};

export type FilterConfig<TData extends RowData = any, TValue = any, TShareContext = any> =
    | TextFilterConfig
    | NumberFilterConfig
    | MonetaryValueFilterConfig
    | DateFilterConfig
    | EnumFilterConfig<TData, TValue, TShareContext>
    | ArraryFilterConfig<TData, TValue, TShareContext>
    | QuantityFilterConfig;

export type TextFilterConfig = {
    dataType: 'text';
    defaultFilterFn: SupportedFilterFn.includesString;
};
export type NumberFilterConfig = {
    dataType: 'number';
    defaultFilterFn: SupportedFilterFn.inNumberRange;
};
export type MonetaryValueFilterConfig = {
    dataType: 'monetaryValue';
    defaultFilterFn: SupportedFilterFn.inMonetaryValueRange;
};
export type DateFilterConfig = {
    dataType: 'date';
    defaultFilterFn: SupportedFilterFn.inDateRange;
};
export type EnumFilterConfig<TData extends RowData = any, TValue = any, TShareContext = any> = {
    dataType: 'enum';
    defaultFilterFn: SupportedFilterFn.equalsAny;
    /** Defaults to the column's unique values, `facetedValues`. */
    options?:
        | Array<TValue>
        | ((props: {
              column: Column<TData, TValue>;
              facetedValues: Array<TValue>;
              sharedContext: TShareContext;
          }) => Array<TValue>);

    getOptionLabel: (option: TValue) => string;

    renderOption?: (option: TValue) => JSX.Element;

    /** The count shown next to the option in the filter, e.g. from the backend when paginated. */
    getOptionCount?: (option: TValue, sharedContext: TShareContext) => number | undefined;

    /** Decides which options are the same, so the filter lists each once. */
    getOptionKey?: (option: TValue) => string | number | boolean;

    /**
     * Gets values to be used in the global filter for a given row.
     */
    additionalSearchValues?: (row: Row<TData>) => string[] | undefined;
};
export type ArraryFilterConfig<TData extends RowData = any, TValue = any, TShareContext = any> = {
    dataType: 'array';
    defaultFilterFn: SupportedFilterFn.arrIncludesSome;
    /** Defaults to the column's unique values, `facetedValues`. */
    options?:
        | TValue
        | ((props: { column: Column<TData, TValue>; facetedValues: TValue; sharedContext: TShareContext }) => TValue);

    getOptionLabel: (option: TValue extends Array<infer U> ? U : never) => string;

    renderOption?: (option: TValue extends Array<infer U> ? U : never) => JSX.Element;

    /** The count shown next to the option in the filter, e.g. from the backend when paginated. */
    getOptionCount?: (
        option: TValue extends Array<infer U> ? U : never,
        sharedContext: TShareContext,
    ) => number | undefined;

    /** Decides which options are the same, so the filter lists each once. */
    getOptionKey?: (option: TValue extends Array<infer U> ? U : never) => string | number | boolean;
};
export type QuantityFilterConfig = {
    dataType: 'quantity';
    defaultFilterFn: SupportedFilterFn.inQuantityRange;
};

export const FilterFnByDataType: Record<ColumnMeta<DataStackFeatures, any, unknown>['dataType'], SupportedFilterFn[]> =
    {
        text: [SupportedFilterFn.includesString, SupportedFilterFn.isEmptyString, SupportedFilterFn.isNotEmptyString],
        number: [SupportedFilterFn.inNumberRange],
        monetaryValue: [SupportedFilterFn.inMonetaryValueRange],
        date: [SupportedFilterFn.inDateRange, SupportedFilterFn.isInLastDays, SupportedFilterFn.isOlderThanDays],
        enum: [SupportedFilterFn.equalsAny, SupportedFilterFn.equalsNone],
        array: [SupportedFilterFn.arrIncludesSome, SupportedFilterFn.arrIncludesAll, SupportedFilterFn.arrExcludesSome],
        quantity: [SupportedFilterFn.inQuantityRange],
        availability: [SupportedFilterFn.inNumberRange],
        generic: [],
    };

type Prettify<T> = {
    [K in keyof T]: T[K];
} & {};

export function getFilterConfig<
    TData extends RowData,
    TValue,
    TShareContext,
    K extends FilterConfig<TData, TValue, TShareContext>['dataType'],
>(
    column: Column<TData, TValue>,
    dataType: K,
): Prettify<FilterConfig<TData, TValue, TShareContext> & { dataType: K }> | null {
    if (!column.getCanFilter() || column.columnDef.meta?.filterConfig?.dataType !== dataType) {
        return null;
    }

    return column.columnDef.meta.filterConfig as FilterConfig<TData, TValue, TShareContext> & { dataType: K };
}

export const FilterFnOptionTranslations: Record<SupportedFilterFn, MessageDescriptor> = {
    [SupportedFilterFn.includesString]: defineMessage({ message: 'contains' }),
    [SupportedFilterFn.isEmptyString]: defineMessage({ message: 'is empty' }),
    [SupportedFilterFn.isNotEmptyString]: defineMessage({ message: 'is not empty' }),
    [SupportedFilterFn.inNumberRange]: defineMessage({ message: 'is between' }),
    [SupportedFilterFn.inMonetaryValueRange]: defineMessage({ message: 'is between' }),
    [SupportedFilterFn.inDateRange]: defineMessage({ message: 'is between' }),
    [SupportedFilterFn.isInLastDays]: defineMessage({ message: 'is in the last' }),
    [SupportedFilterFn.isOlderThanDays]: defineMessage({ message: 'is older than' }),
    [SupportedFilterFn.equalsAny]: defineMessage({ message: 'is' }),
    [SupportedFilterFn.equalsNone]: defineMessage({ message: 'is not' }),
    [SupportedFilterFn.arrIncludesSome]: defineMessage({ message: 'contains' }),
    [SupportedFilterFn.arrIncludesAll]: defineMessage({ message: 'contains all' }),
    [SupportedFilterFn.arrExcludesSome]: defineMessage({ message: 'does not contain' }),
    [SupportedFilterFn.inQuantityRange]: defineMessage({ message: 'is between' }),
};
